import React, { useState, useRef, useEffect } from "react";
import { noop } from "lodash";
import PropTypes from "prop-types";
import { MdOutlineFileCopy, MdDeleteOutline } from "react-icons/md";
import { IoMdDownload } from "react-icons/io";
import {
  Button,
  InputGroup,
  Input,
  Notification,
  toast,
  Tooltip,
} from "components/ui";
import { motion, AnimatePresence } from "framer-motion";

const InputGroupFile = (props) => {
  const {
    label,
    currentFiles = [],
    onChange = noop,
    onDelete = noop,
    onDownload = noop,
    disabled,
    errorMessage = null,
    invalid = null,
    ...rest
  } = props;
  const [files, setFiles] = useState(currentFiles || []);

  useEffect(() => {
    // Always sync with parent's currentFiles prop
    setFiles(currentFiles || []);
  }, [currentFiles]);

  const hiddenFileInput = useRef(null);

  const handleClick = () => {
    if (disabled) return;
    hiddenFileInput.current.click();
  };

  const handleChange = (event) => {
    if (event.target && event.target.files.length === 0) return;

    const fileUploaded = event.target.files[0];
    const maxFileSize = 1000 * 1000 * 10; // 10MB

    if (fileUploaded.size > maxFileSize) {
      return toast.push(
        <Notification title="Error" type="danger" duration={2500}>
          Ukuran file tidak boleh lebih dari 10MB!
        </Notification>,
        {
          placement: "top-end",
        }
      );
    }

    // Validate file type if accept prop is provided
    if (props.accept) {
      const allowedExtensions = props.accept
        .split(",")
        .map((ext) => ext.trim().toLowerCase());
      const fileName = fileUploaded.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf("."));

      if (!allowedExtensions.includes(fileExtension)) {
        return toast.push(
          <Notification title="Error" type="danger" duration={2500}>
            Format file tidak diizinkan! Format yang diizinkan: {props.accept}
          </Notification>,
          {
            placement: "top-end",
          }
        );
      }
    }

    const newFiles = [...files, fileUploaded];
    setFiles(newFiles);
    onChange(newFiles);
  };

  const enterStyle = { opacity: 1, marginTop: 3, bottom: -21 };
  const exitStyle = { opacity: 0, marginTop: -10 };
  const initialStyle = exitStyle;

  return (
    <div className="relative single-upload">
      <label className="block text-sm font-normal text-[#525252] mb-1">
        {label}
      </label>
      {files.length > 0 && (
        <div className="border border-[#d4d4d4] px-3 py-2 rounded-[8px] mb-2">
          {files.map((file, index) => (
            <div
              className={`flex flex-row justify-between ${
                files.length - 1 !== index ? "mb-3" : ""
              }`}
              key={`key-${index}`}
            >
              <div className="col-span-2 flex flex-row items-center gap-2 truncate">
                <MdOutlineFileCopy size={18} />
                <p className="flex-1 text-xs font-normal text-[#525252] truncate">
                  {file.name}
                </p>
              </div>
              <div className="col-span-1 flex flex-row gap-2 justify-end">
                {onDownload !== noop && file.uuid && (
                  <Tooltip title="Download">
                    <button
                      className="bg-[#e0edfd] rounded-[6px] px-3 py-2"
                      onClick={() => onDownload(file)}
                      type="button"
                    >
                      <IoMdDownload color="#4e84c3" />
                    </button>
                  </Tooltip>
                )}
                {!disabled && (
                  <button
                    className="bg-[#fcdada] px-3 py-1 rounded-[6px]"
                    onClick={() => {
                      if (disabled) return;
                      const updatedFiles = files.filter((_, i) => i !== index);
                      const deletedFile = files.find((_, i) => i === index);

                      onDelete(deletedFile, updatedFiles);
                      setFiles(updatedFiles);
                    }}
                    type="button"
                  >
                    <MdDeleteOutline color="#bf3636" size={18} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <InputGroup className="mb-4">
        <Button
          className="bg-[#e5e5e5] border border-[#a3a3a3] text-[#646464] text-sm font-normal"
          onClick={handleClick}
          type="button"
        >
          Pilih File
        </Button>
        <Input
          type="text"
          className={`cursor-pointer border border-[#a3a3a3] !rounded-r-[8px] ${
            disabled ? "bg-[#f3f4f6]" : ""
          }`}
          placeholder="Pilih dokumen file"
          onClick={handleClick}
          readOnly
        />
        <input
          type="file"
          ref={hiddenFileInput}
          onChange={handleChange}
          className="hidden"
          disabled={disabled}
          accept={props.accept}
          {...rest}
        />
      </InputGroup>
      <AnimatePresence exitBeforeEnter>
        {invalid && (
          <motion.div
            className="form-explain"
            initial={initialStyle}
            animate={enterStyle}
            exit={exitStyle}
            transition={{ duration: 0.15, type: "tween" }}
          >
            {errorMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

InputGroupFile.propTypes = {
  currentFiles: PropTypes.array,
  onDelete: PropTypes.func,
  onDownload: PropTypes.func,
};

export default InputGroupFile;
